<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useGroupContextStore } from '@/stores/groupContext'

/**
 * Where /admin lands, and usually where it does not stay (SPEC §8.9).
 *
 * An organizer with one group — which is nearly all of them — should never see
 * this screen. It resolves what they have and sends them into it, so the common
 * case has no step in it at all. Only somebody with several groups, or none,
 * has anything to decide here.
 */

const router = useRouter()
const groupContext = useGroupContextStore()

onMounted(async () => {
  await groupContext.ensureLoaded()

  if (groupContext.isSingleGroup) {
    await router.replace({
      name: 'group-dashboard',
      params: { groupId: groupContext.groups[0]!.id },
    })
  }
})
</script>

<template>
  <v-container class="py-10" style="max-width: 44rem">
    <div
      v-if="!groupContext.isResolved && !groupContext.error"
      class="py-8 text-body-2"
      style="opacity: 0.75"
      role="status"
    >
      Loading your groups…
    </div>

    <!-- A failed load is not an empty account. Showing the set-up prompt here
         would invite somebody to create a second group they already have. -->
    <div
      v-else-if="groupContext.error"
      data-testid="dashboard-error"
      role="alert"
      class="pa-3 rounded text-body-2"
      style="background-color: rgb(var(--v-theme-error)); color: rgb(var(--v-theme-on-error))"
    >
      {{ groupContext.error }}
    </div>

    <template v-else-if="!groupContext.hasGroups">
      <h1 class="text-h5 mb-3">Let's set up your group</h1>
      <p class="text-body-1 mb-6" style="opacity: 0.85">
        A group is the family that eats together. Everything else — the people, the meals, who is
        bringing what — hangs off it.
      </p>

      <v-card class="pa-5" data-testid="empty-state">
        <div class="text-overline mb-2">First step</div>
        <h2 class="text-h6 mb-2">Create your group</h2>
        <p class="text-body-2 mb-4" style="opacity: 0.75">
          A name and a time zone. It takes a moment and you can change both later.
        </p>
        <v-btn color="primary" :to="{ name: 'create-group' }">Create group</v-btn>
      </v-card>
    </template>

    <!-- Several groups: choose one. A single-group organizer was redirected
         before this rendered. -->
    <template v-else>
      <h1 class="text-h5 mb-6">Which group?</h1>

      <v-card
        v-for="group in groupContext.groups"
        :key="group.id"
        class="pa-5 mb-3"
        data-testid="group-choice"
      >
        <h2 class="text-h6 mb-1">{{ group.name }}</h2>
        <p class="text-body-2 mb-4" style="opacity: 0.75">{{ group.timeZoneId }}</p>
        <v-btn
          color="primary"
          size="small"
          :to="{ name: 'group-dashboard', params: { groupId: group.id } }"
        >
          Open
        </v-btn>
      </v-card>

      <v-btn variant="text" class="mt-2" :to="{ name: 'create-group' }">
        Create another group
      </v-btn>
    </template>
  </v-container>
</template>
